/**
 * The board: 64 square meshes, the surrounding plinth, rank/file labels, and
 * the highlight overlays used for selection, legal moves and check.
 *
 * World space maps a1 to (-3.5, 0, 3.5) and h8 to (3.5, 0, -3.5), so White
 * advances toward negative Z and the default camera sits behind White.
 */

import * as THREE from 'three';
import { fileOf, rankOf, idx } from '../game/chess.js';
import { mat, stoneMat, goldMat } from './materials.js';

export const SQUARE = 1;
export const BOARD_HALF = 4;

export const squareToWorld = (i) =>
  new THREE.Vector3(fileOf(i) - 3.5, 0, 3.5 - (rankOf(i) - 1));

export function worldToSquare(point) {
  const f = Math.round(point.x + 3.5);
  const r = Math.round(3.5 - point.z) + 1;
  if (f < 0 || f > 7 || r < 1 || r > 8) return null;
  return idx(f, r);
}

const HIGHLIGHT = {
  select: 0x5ad2ff,
  move: 0x67e08a,
  capture: 0xff5a4a,
  check: 0xff2d2d,
  last: 0xf0c24a
};

export class Board {
  constructor() {
    this.group = new THREE.Group();
    this.squares = [];
    this.overlays = new Map(); // square index -> overlay mesh
    this.pickTargets = [];

    this.buildSquares();
    this.buildFrame();
    this.buildLabels();
  }

  buildSquares() {
    const light = mat(0xe6d7b8, { rough: 0.6, metal: 0.05 });
    const dark = mat(0x4a5a72, { rough: 0.6, metal: 0.05 });
    const geom = new THREE.BoxGeometry(SQUARE, 0.12, SQUARE);

    for (let i = 0; i < 64; i++) {
      const isLight = (fileOf(i) + rankOf(i)) % 2 === 1;
      const tile = new THREE.Mesh(geom, isLight ? light : dark);
      tile.position.copy(squareToWorld(i)).setY(-0.06);
      tile.receiveShadow = true;
      tile.userData.square = i;
      this.group.add(tile);
      this.squares.push(tile);
      this.pickTargets.push(tile);
    }
  }

  buildFrame() {
    const frameMat = stoneMat(0x2e2a24);
    const outer = 9.1;
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(outer, 0.24, outer),
      frameMat
    );
    slab.position.y = -0.13;
    slab.receiveShadow = true;
    this.group.add(slab);

    // A gold beam along each edge of the playing area.
    const beam = goldMat(0xc9a227);
    const span = 8.36;
    const thickness = 0.1;
    for (const [w, d, x, z] of [
      [span, thickness, 0, -4.18],
      [span, thickness, 0, 4.18],
      [thickness, span, -4.18, 0],
      [thickness, span, 4.18, 0]
    ]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(w, 0.06, d), beam);
      rail.position.set(x, 0.0, z);
      rail.castShadow = true;
      this.group.add(rail);
    }
  }

  buildLabels() {
    // Labels are drawn once into a canvas texture and mapped onto the frame.
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1024;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, 1024, 1024);
    ctx.fillStyle = '#d8c9a0';
    ctx.font = 'bold 34px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const inner = 1024 * (8 / 9.1);
    const start = (1024 - inner) / 2;
    const step = inner / 8;
    const edge = start / 2;

    for (let f = 0; f < 8; f++) {
      const x = start + step * (f + 0.5);
      const letter = 'abcdefgh'[f];
      ctx.fillText(letter, x, 1024 - edge);
      ctx.fillText(letter, x, edge);
    }
    for (let r = 0; r < 8; r++) {
      const y = start + step * (r + 0.5);
      const num = String(8 - r);
      ctx.fillText(num, edge, y);
      ctx.fillText(num, 1024 - edge, y);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(9.1, 9.1),
      new THREE.MeshBasicMaterial({ map: texture, transparent: true })
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -0.005;
    this.group.add(plane);
  }

  /** Draws a translucent disc or ring over a square. */
  addOverlay(square, kind) {
    this.clearOverlay(square);
    const color = HIGHLIGHT[kind] ?? HIGHLIGHT.move;
    const geometry =
      kind === 'capture'
        ? new THREE.RingGeometry(0.34, 0.46, 24)
        : new THREE.CircleGeometry(kind === 'move' ? 0.16 : 0.47, 24);

    const overlay = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: kind === 'move' ? 0.75 : 0.45,
        depthWrite: false
      })
    );
    overlay.rotation.x = -Math.PI / 2;
    overlay.position.copy(squareToWorld(square)).setY(0.011);
    overlay.userData.square = square;
    overlay.userData.kind = kind;
    this.group.add(overlay);
    this.overlays.set(`${square}:${kind}`, overlay);
    return overlay;
  }

  clearOverlay(square) {
    for (const [key, overlay] of this.overlays) {
      if (overlay.userData.square === square) {
        this.group.remove(overlay);
        overlay.geometry.dispose();
        overlay.material.dispose();
        this.overlays.delete(key);
      }
    }
  }

  clearOverlays(kind = null) {
    for (const [key, overlay] of this.overlays) {
      if (kind && overlay.userData.kind !== kind) continue;
      this.group.remove(overlay);
      overlay.geometry.dispose();
      overlay.material.dispose();
      this.overlays.delete(key);
    }
  }

  /** Pulses the check and selection overlays so they read as live. */
  update(elapsed) {
    for (const overlay of this.overlays.values()) {
      const kind = overlay.userData.kind;
      if (kind === 'check') {
        overlay.material.opacity = 0.35 + Math.sin(elapsed * 6) * 0.25;
      } else if (kind === 'select') {
        overlay.material.opacity = 0.35 + Math.sin(elapsed * 3) * 0.12;
      }
    }
  }
}
